import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import translate from '@src/services/translate'
import type { TRootState } from '..'
import { apiStateSelectors } from '../ApiStateStore'

import { CreditRslaWorksheet } from '@src/data/types/CreditRslaWorksheet'
import { NormsMessage } from '../../types'
import { ParameterizedMessage } from '../../types/ParameterizedMessage'
import { appSelectors } from '../AppStore'

export const SAVE_CREDIT_RSLA_WORKSHEET = 'creditRslaWorksheet/save'
export const GET_CREDIT_RSLA_WORKSHEET = 'creditRslaWorksheet/getById'

export const CreditRslaWorksheetSlice = createSlice({
  name: 'creditRslaWorksheet',
  initialState: {
    current: null as CreditRslaWorksheet | null,
  },
  reducers: {
    setCurrent: (state, action: PayloadAction<CreditRslaWorksheet | null>) => {
      state.current = action.payload
    },
  },
})

export default CreditRslaWorksheetSlice.reducer

export const creditRslaWorksheetActions = CreditRslaWorksheetSlice.actions

const creditRslaWorksheetSelectorsPartial = {
  isSavingPersonalLoanWorksheet: (state: TRootState) => apiStateSelectors.isLoading(state, SAVE_CREDIT_RSLA_WORKSHEET),
  getCurrent: (state: TRootState) => state.creditRslaWorksheet.current,
}

function getParameterizedNormMessage(message: ParameterizedMessage): string {
  const translatedMessage = translate([`worksheet.norms.${message.id}`])

  let messageSuffix = ' | '
  message.parameters.forEach((p, i) => {
    if (i >= 1) messageSuffix = messageSuffix.concat(', ')

    const suffix = translate(`worksheet.paramNames.${p.name}`, { val: p.value })
    messageSuffix = messageSuffix.concat(suffix)
  })

  return translatedMessage.concat(messageSuffix)
}

const getCurrentWorksheetNormWarningMessages = createSelector(
  [creditRslaWorksheetSelectorsPartial.getCurrent, appSelectors.getCurrentLang],
  (currentWorksheet) => {
    const worksheetNormMessages: NormsMessage[] = []
    currentWorksheet?.normsDecision?.warningMessages.forEach((wr) => {
      const builtMessage = getParameterizedNormMessage(wr)
      const worksheetNormMessage: NormsMessage = {
        id: wr.id,
        message: builtMessage,
        applicantType: null,
      }
      worksheetNormMessages.push(worksheetNormMessage)
    })

    return worksheetNormMessages
  },
)

const getCurrentWorksheetNormRefusalMessages = createSelector(
  [creditRslaWorksheetSelectorsPartial.getCurrent, appSelectors.getCurrentLang],
  (currentWorksheet) => {
    const worksheetNormMessages: NormsMessage[] = []
    currentWorksheet?.normsDecision?.refusalMessages.forEach((wr) => {
      const builtMessage = getParameterizedNormMessage(wr)
      const worksheetNormMessage: NormsMessage = {
        id: wr.id,
        message: builtMessage,
        applicantType: null,
      }
      worksheetNormMessages.push(worksheetNormMessage)
    })

    return worksheetNormMessages
  },
)

const getCurrentWorksheetNormRefusalAndWarningMessages = createSelector(
  [creditRslaWorksheetSelectorsPartial.getCurrent, appSelectors.getCurrentLang],
  (currentWorksheet) => {
    const warningsAndRefusals = currentWorksheet?.normsDecision?.warningMessages?.concat(
      currentWorksheet?.normsDecision?.refusalMessages,
    )

    const worksheetNormMessages: NormsMessage[] = []
    warningsAndRefusals?.forEach((wr) => {
      const builtMessage = getParameterizedNormMessage(wr)
      const worksheetNormMessage: NormsMessage = {
        id: wr.id,
        message: builtMessage,
        applicantType: null,
      }
      worksheetNormMessages.push(worksheetNormMessage)
    })
    return worksheetNormMessages
  },
)

export const creditRslaWorksheetSelectors = {
  ...creditRslaWorksheetSelectorsPartial,
  getCurrentWorksheetNormRefusalAndWarningMessages,
  getCurrentWorksheetNormWarningMessages,
  getCurrentWorksheetNormRefusalMessages,
}
